import PropTypes from "prop-types";
import { memo, useEffect, useState } from "react";
import Paragraphs from "app/pages/.shared/Paragraphs";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { ProductsCarousel } from "app/pages/.shared/ProductsCarousel";
import IconRight from "app/pages/.shared/static/icons/IconRight";
import ProductCard from "app/pages/.shared/ProductCard/ProductCard";
import ProductList from "app/pages/Listing/ProductList/ProductList";
import ProductListBlockFilter from "app/pages/.shared/DynamicBlocks/blocks/ProductListBlock/ProductListBlockFilter";
import classNames from "classnames";
import "./ProductListBlock.scss";
import Button, {
	BUTTON_ICON_ALIGNMENTS,
	BUTTON_SIZES,
	BUTTON_VARIANTS,
} from "app/pages/.shared/form/Button";
import { FormattedMessage } from "react-intl";
import {
	FilterTrigger,
	FilterDrawer,
	StickyFilterButtonTrigger,
	useFacets,
} from "app/features/product/productFilters";
import { useAnalytics } from "app/utils/analytics/useAnalytics";

const ProductListBlock = ({
	title,
	shortVersion,
	shouldHideProductPriceBanner,
	products = [],
	redirectUrl,
	displayFilters = false,
	isMobile,
}) => {
	const [productsFiltered, setProductsFiltered] = useState(products);
	const { track } = useAnalytics();

	// sans ce useEffect, productsFiltered est tout le temps vide
	useEffect(() => {
		setProductsFiltered(products);
	}, [products]);

	const { filteredProducts } = useFacets();

	const productListBLockTitleClassNames = classNames({
		"product-list-block__title": true,
		"product-list-block__title--with-filter": displayFilters,
	});

	const handleOpenFilterDrawer = () => {
		track("product-filter-open");
	};

	const handleFilterChange = (facetName, facetValue) => {
		track("pp-filter-facet-clicked", { facetName, facetValue });
	};

	if (products.length === 0) {
		return false;
	}

	let productsNode = false;

	if (isMobile) {
		if (shortVersion) {
			productsNode = (
				<ProductsCarousel
					products={filteredProducts}
					hidePriceBanner={shouldHideProductPriceBanner}
					noSwiping={!isMobile}
				/>
			);
		} else {
			productsNode = (
				<ProductList
					products={filteredProducts}
					hidePriceBanner={shouldHideProductPriceBanner}
					hideMembershipBlock={true}
				/>
			);
		}
	} else if (shortVersion) {
		productsNode = (
			<div className="product-list-block__products">
				{productsFiltered.map((product, index) => {
					return (
						<div key={product.id} data-testid={`product-${product.uri}`}>
							<ProductCard
								product={product}
								position={index}
								hidePriceBanner={shouldHideProductPriceBanner}
							/>
						</div>
					);
				})}
			</div>
		);
	} else {
		productsNode = (
			<ProductList
				products={productsFiltered}
				hidePriceBanner={shouldHideProductPriceBanner}
				hideMembershipBlock={true}
			/>
		);
	}

	return (
		<div className="product-list-block" data-testid="product-list-block">
			<div className="product-list-block__container">
				<div className={productListBLockTitleClassNames}>
					{title && (
						<Typography
							variant={TYPOGRAPHY_VARIANTS.XL3}
							variantMD={TYPOGRAPHY_VARIANTS.XL4}
							isBold
							component="h2"
						>
							<Paragraphs paragraphs={title} enableHTMLFormatting />
						</Typography>
					)}

					{!isMobile && shortVersion && (
						<div className="product-list-block__view-all-offers-link">
							<Button
								variant={BUTTON_VARIANTS.BORDERLESS}
								size={BUTTON_SIZES.LARGE}
								to={redirectUrl}
								label={
									<FormattedMessage id="products.carousel.view.all.offers.label" />
								}
								alignIcon={BUTTON_ICON_ALIGNMENTS.RIGHT}
								icon={<IconRight />}
							/>
						</div>
					)}
				</div>
			</div>

			{displayFilters &&
				!shortVersion &&
				(isMobile ? (
					<>
						<FilterTrigger asChild onClick={handleOpenFilterDrawer}>
							<StickyFilterButtonTrigger />
						</FilterTrigger>
						<FilterDrawer isSinglePanelMode onChange={handleFilterChange} />
					</>
				) : (
					<ProductListBlockFilter
						products={products}
						filterProduct={setProductsFiltered}
					/>
				))}

			<div className="product-list-block__container">
				{productsNode}

				{isMobile && shortVersion && (
					<div className="product-list-block__view-all-offers-button">
						<Button
							variant={BUTTON_VARIANTS.STROKE}
							size={BUTTON_SIZES.LARGE}
							to={redirectUrl}
							label={
								<FormattedMessage id="products.carousel.view.all.offers.label" />
							}
							alignIcon={BUTTON_ICON_ALIGNMENTS.RIGHT}
							icon={<IconRight />}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

ProductListBlock.propTypes = {
	products: PropTypes.array,
	title: PropTypes.string,
	redirectUrl: PropTypes.string,
	shouldHideProductPriceBanner: PropTypes.bool,
	shortVersion: PropTypes.bool,
	displayFilters: PropTypes.bool,
	isMobile: PropTypes.bool,
};

export default memo(ProductListBlock);
